import { useCallback, useMemo, useRef, useState } from 'react';
import type {
  FieldName,
  FlowStep,
  FormErrors,
  PayPeriod,
  PaymentFormValues,
  PaymentType,
} from '../types/payment';
import {
  deriveValues,
  derivedFields,
  emptyValues,
  getPrimaryAmount,
  getVisibility,
  validate,
} from '../utils/paymentConfig';
import { formatAmountInput } from '../utils/currency';
import { loans } from '../data/payPeriods';

interface Options {
  payPeriod: PayPeriod;
  initialPaymentType?: PaymentType | null;
  initialValues?: Partial<PaymentFormValues>;
  /** Set to true to make the next save attempt fail, for demoing the error state. */
  forceFailure?: boolean;
}

/**
 * Seeds the form from preset values while keeping every calculated field calculated,
 * so Net Commission always matches the commission model's formula.
 */
function buildInitialState(
  payPeriod: PayPeriod,
  paymentType: PaymentType | null,
  initialValues?: Partial<PaymentFormValues>,
) {
  const derived = new Set<string>(derivedFields);
  const manual = new Set(
    Object.keys(initialValues ?? {}).filter((field) => !derived.has(field)),
  );
  const visibility = getVisibility(paymentType, payPeriod);
  const values = deriveValues(
    { ...emptyValues, ...payPeriodDefaults(payPeriod, paymentType), ...initialValues },
    manual,
    { corpFeeReducesNet: visibility.commissionCorpFee },
  );
  return { values, manual };
}

/** Values seeded from the pay period record itself, e.g. the configured corp fee. */
function payPeriodDefaults(
  payPeriod: PayPeriod,
  paymentType: PaymentType | null,
): Partial<PaymentFormValues> {
  const visibility = getVisibility(paymentType, payPeriod);
  if (!visibility.commissionCorpFee) return {};
  return { corpFee: formatAmountInput(String(payPeriod.defaultCorpFee)) };
}

export function useAddPaymentForm({
  payPeriod,
  initialPaymentType = null,
  initialValues,
  forceFailure = false,
}: Options) {
  const seed = useRef(
    buildInitialState(payPeriod, initialPaymentType, initialValues),
  ).current;
  const [paymentType, setPaymentTypeState] = useState<PaymentType | null>(
    initialPaymentType,
  );
  const [values, setValues] = useState<PaymentFormValues>(seed.values);
  const [errors, setErrors] = useState<FormErrors>({});
  const [step, setStep] = useState<FlowStep>('form');
  const [saving, setSaving] = useState(false);
  const [savedCount, setSavedCount] = useState(0);
  const manualFields = useRef<Set<string>>(new Set(seed.manual));

  const visibility = useMemo(
    () => getVisibility(paymentType, payPeriod),
    [paymentType, payPeriod],
  );

  const setPaymentType = useCallback(
    (next: PaymentType) => {
      setPaymentTypeState(next);
      manualFields.current = new Set();
      setValues(
        deriveValues(
          { ...emptyValues, ...payPeriodDefaults(payPeriod, next) },
          manualFields.current,
          { corpFeeReducesNet: getVisibility(next, payPeriod).commissionCorpFee },
        ),
      );
      setErrors({});
    },
    [payPeriod],
  );

  const deriveOptions = useMemo(
    () => ({ corpFeeReducesNet: visibility.commissionCorpFee }),
    [visibility.commissionCorpFee],
  );

  const setValue = useCallback((field: FieldName, value: string | null) => {
    // Selecting a loan pulls Gross Commission from the loan record and re-derives net.
    if (field === 'loanId') {
      const loan = loans.find((item) => item.id === value) ?? null;
      manualFields.current.add('loanId');
      manualFields.current.delete('grossCommission');
      manualFields.current.delete('netCommission');
      setValues((prev) =>
        deriveValues(
          {
            ...prev,
            loanId: value,
            grossCommission: loan ? formatAmountInput(String(loan.grossCommission)) : '',
          },
          manualFields.current,
          deriveOptions,
        ),
      );
      setErrors((prev) => {
        if (!prev.loanId) return prev;
        const next = { ...prev };
        delete next.loanId;
        return next;
      });
      return;
    }

    manualFields.current.add(field);
    setValues((prev) => {
      const updated = { ...prev, [field]: value } as PaymentFormValues;
      return deriveValues(updated, manualFields.current, deriveOptions);
    });
    setErrors((prev) => {
      if (!prev[field]) return prev;
      const next = { ...prev };
      delete next[field];
      return next;
    });
  }, [deriveOptions]);

  const formatOnBlur = useCallback((field: FieldName) => {
    setValues((prev) => {
      const raw = prev[field];
      if (typeof raw !== 'string') return prev;
      const formatted = formatAmountInput(raw);
      if (formatted === raw) return prev;
      return { ...prev, [field]: formatted } as PaymentFormValues;
    });
  }, []);

  const runValidation = useCallback((): boolean => {
    if (!paymentType) {
      return false;
    }
    const next = validate(values, paymentType, payPeriod);
    setErrors(next);
    return Object.keys(next).length === 0;
  }, [payPeriod, paymentType, values]);

  const goToReview = useCallback(() => {
    if (runValidation()) setStep('review');
  }, [runValidation]);

  const save = useCallback(() => {
    if (!runValidation()) {
      setStep('form');
      return;
    }
    setSaving(true);
    window.setTimeout(() => {
      setSaving(false);
      if (forceFailure) {
        setStep('error');
      } else {
        setSavedCount((c) => c + 1);
        setStep('success');
      }
    }, 900);
  }, [forceFailure, runValidation]);

  const reset = useCallback(() => {
    setPaymentTypeState(null);
    setValues({ ...emptyValues });
    setErrors({});
    manualFields.current = new Set();
    setStep('form');
  }, []);

  const primaryAmount = paymentType ? getPrimaryAmount(values, paymentType) : 0;

  return {
    paymentType,
    setPaymentType,
    values,
    setValue,
    formatOnBlur,
    errors,
    errorCount: Object.keys(errors).length,
    visibility,
    step,
    setStep,
    saving,
    savedCount,
    primaryAmount,
    goToReview,
    save,
    reset,
  };
}
